import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { cn } from '../lib/utils';
import { Icon, type IconName } from './icons';

export interface RowMenuItem {
  label: string;
  onSelect: () => void;
  icon?: IconName;
  /** Destructive — rendered last, below a separator, in the danger colour. */
  danger?: boolean;
  disabled?: boolean;
}

export interface RowMenuProps {
  /** What the menu acts on, for the trigger's accessible name ("Actions for X"). */
  label: string;
  items: RowMenuItem[];
  className?: string;
  /** A worded trigger ("More") instead of the ⋯ icon — for a toolbar overflow menu.
   * The words are then the trigger's accessible name. */
  triggerText?: string;
}

/**
 * The ⋯ menu on a table row, checklist item or card (DataRoom UI "RowMenu"):
 * every per-row action in one place so rows stay one line. WAI-ARIA menu button:
 * the trigger has aria-haspopup/expanded; the menu takes focus on its first item;
 * ↑/↓ (and Home/End) move, Enter/Space select, typing a letter jumps to the next
 * item starting with it; Escape or Tab closes it and Escape returns focus to the
 * trigger; a click outside closes it. Destructive items sit last, separated.
 */
export function RowMenu({ label, items, className, triggerText }: RowMenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const ordered = [...items.filter((i) => !i.danger), ...items.filter((i) => i.danger)];
  const firstDanger = ordered.findIndex((i) => i.danger);

  const menuItems = () =>
    Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? []);

  useEffect(() => {
    if (!open) return;
    menuItems().find((b) => !b.disabled)?.focus();
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const list = menuItems().filter((b) => !b.disabled);
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    const move = (to: number) => list[(to + list.length) % list.length]?.focus();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(at + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(at - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      move(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      move(list.length - 1);
    } else if (e.key === 'Escape') {
      // Handled here so a surrounding dialog does not also close.
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      close(true);
    } else if (e.key === 'Tab') {
      close(false);
    } else if (e.key.length === 1 && /\S/.test(e.key)) {
      const ch = e.key.toLowerCase();
      for (let i = 1; i <= list.length; i++) {
        const cand = list[(at + i) % list.length];
        if (cand?.textContent?.trim().toLowerCase().startsWith(ch)) {
          cand.focus();
          break;
        }
      }
    }
  };

  return (
    <div ref={rootRef} className={cn('relative inline-block', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={triggerText ? undefined : `Actions for ${label}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={
          triggerText
            ? 'inline-flex h-8 items-center gap-1 rounded-md border border-input bg-surface px-3 text-sm font-medium text-ink hover:bg-surface-2 max-md:min-h-tap'
            : 'inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-2 hover:bg-surface-2 hover:text-ink max-md:h-11 max-md:w-11'
        }
      >
        {triggerText ? (
          <>
            {triggerText}
            <Icon name="chevronDown" size={14} />
          </>
        ) : (
          <Icon name="more" />
        )}
      </button>
      {open ? (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={`Actions for ${label}`}
          onKeyDown={onMenuKey}
          className="absolute right-0 top-full z-40 mt-1 min-w-[200px] rounded-md border bg-surface p-1 text-left shadow-md"
        >
          {ordered.map((item, i) => (
            <div key={item.label}>
              {i === firstDanger && i > 0 ? (
                <div role="separator" className="my-1 h-px bg-line" />
              ) : null}
              <button
                type="button"
                role="menuitem"
                tabIndex={-1}
                disabled={item.disabled}
                onClick={() => {
                  close(true);
                  item.onSelect();
                }}
                className={cn(
                  'flex min-h-9 w-full items-center gap-2.5 whitespace-nowrap rounded-sm px-2.5 text-left text-sm hover:bg-surface-2 focus:bg-surface-2 focus:outline-none disabled:opacity-50 max-md:min-h-tap',
                  item.danger ? 'text-danger' : 'text-ink',
                )}
              >
                {item.icon ? <Icon name={item.icon} /> : null}
                {item.label}
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
